// File: src/components/layout/Sidebar.jsx
// Updated with mobile menu support

import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import '../../styles/unified.css';
import { 
  LayoutDashboard, 
  Calendar, 
  BookOpen, 
  GraduationCap,
  Users,
  CreditCard, 
  FileText, 
  Bell, 
  MoreHorizontal,
  Target,
  ChevronLeft,
  ChevronRight,
  X,
  TrendingUp,
  Wallet,
  ShieldCheck
} from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { AUTH0_NAMESPACE } from '../../utils/constants';

const Sidebar = ({ isMobileOpen, onClose }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { user } = useAuth0();
  
  const userRoles = user?.[`${AUTH0_NAMESPACE}/roles`] || [];
  const isServiceProvider = userRoles.includes('ServiceProvider');
  const isAdmin = userRoles.includes('admin');
  
  const menuItems = [
    { 
      name: 'Dashboard', 
      icon: LayoutDashboard, 
      path: '/dashboard',
      roles: ['ServiceProvider', 'ServiceUser']
    },
    { 
      name: 'Manage Configurations', 
      icon: Calendar, 
      path: '/template',
      roles: ['ServiceProvider', 'ServiceProviderAdmin', 'admin']
    },
    { 
      name: 'Bookings', 
      icon: BookOpen, 
      path: '/bookings',
      roles: ['ServiceUser']
    },
    { 
      name: 'My Progress', 
      icon: TrendingUp, 
      path: '/my-progress',
      roles: ['ServiceUser']
    },
    { 
      name: 'My Payments', 
      icon: Wallet, 
      path: '/my-payments',
      roles: ['ServiceUser']
    },
    { 
      name: 'Manage Bookings', 
      icon: BookOpen, 
      path: '/manage-bookings',
      roles: ['ServiceProvider']
    },
    { 
      name: 'Manage Students', 
      icon: GraduationCap, 
      path: '/manage-students',
      roles: ['ServiceProvider','admin']
    },
    { 
      name: 'Manage Instructors', 
      icon: Users, 
      path: '/instructors',
      roles: ['admin','ServiceProviderAdmin']  // Admin only
    },
    { 
      name: 'Manage Admins', 
      icon: Users, 
      path: '/manage-admins',
      roles: ['admin']  // Admin only
    },
    { 
      name: 'Manage Payments', 
      icon: CreditCard, 
      path: '/payments',
      roles: ['ServiceProvider']
    },
    { 
      name: 'Manage Tracker', 
      icon: Target, 
      path: '/manage-tracker',
      roles: ['ServiceProvider']
    },
    { 
      name: 'Tracker Templates', 
      icon: ShieldCheck, 
      path: '/admin-tracker',
      roles: ['admin']
    },
    { 
      name: 'Manage Notifications', 
      icon: Bell, 
      path: '/notifications',
      roles: ['ServiceProvider', 'admin']
    },
    
    { 
      name: 'Reports', 
      icon: FileText, 
      path: '/report',
      roles: ['ServiceProvider']
    },
    { 
      name: 'Others', 
      icon: MoreHorizontal, 
      path: '/others',
      roles: ['ServiceProvider', 'admin']
    }
  ];

  // Filter menu items based on user role
  const visibleMenuItems = menuItems.filter(item => {
    return item.roles.some(role => userRoles.includes(role));
  });

  // Close mobile menu when clicking a link
  const handleNavLinkClick = () => {
    if (window.innerWidth <= 768 && onClose) {
      onClose();
    }
  };

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth <= 768 && isMobileOpen) {
        const sidebar = document.querySelector('.sidebar');
        if (sidebar && !sidebar.contains(event.target)) {
          const menuButton = document.querySelector('.mobile-menu-toggle');
          if (menuButton && !menuButton.contains(event.target)) {
            onClose();
          }
        }
      }
    };

    if (isMobileOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileOpen, onClose]);

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="sidebar-overlay" 
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-content">
          {/* Mobile Close Button */}
          <button 
            className="sidebar-mobile-close"
            onClick={onClose}
            aria-label="Close menu"
          >
            <X size={24} />
          </button>

          {/* Desktop Toggle Button */}
          <button 
            className="sidebar-toggle"
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>

          {/* Menu Items */}
          <nav className="sidebar-nav">
            {visibleMenuItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => 
                    `sidebar-item ${isActive ? 'active' : ''}`
                  }
                  title={isCollapsed ? item.name : ''}
                  onClick={handleNavLinkClick}
                >
                  <Icon size={22} className="sidebar-icon" />
                  {!isCollapsed && <span className="sidebar-text">{item.name}</span>}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;